<template>
    <Layout is-crm :content-class="'content--centered'">
        <div class="container">
            <div class="create-order">
                <Spinner v-if="loading" />
                <form
                    v-if="schema && Object.keys(schema).length"
                    ref="form"
                    class="create-order-form form"
                    @submit.prevent="submitOrder"
                >
                    <div class="order__top">
                        <h3>Create Order</h3>
                        <OrderStatusBadge class="order__state" />
                    </div>
                    <Grid :gap-y="15">
                        <Column v-if="isInnerWorkspace && schema.customer">
                            <FormField
                                name="customer"
                                label="Customer"
                                :error="error"
                                :required="schema.customer.required"
                            >
                                <CustomSelect
                                    v-model="order.customer"
                                    with-filter
                                    emit-prop="id"
                                    view-label="name"
                                    :options="customers"
                                    width="100%"
                                    :first-as-default="customers.length === 1"
                                />
                            </FormField>
                        </Column>
                        <Column v-if="schema.customer_flow">
                            <FormField
                                name="customer"
                                label="Customer Flow"
                                :error="error"
                                :required="schema.customer_flow.required"
                            >
                                <CustomSelect
                                    v-model="order.customer_flow"
                                    with-filter
                                    emit-prop="id"
                                    :options="flows"
                                    :form-value-text="(item) => item.flow.label[$i18n.locale]"
                                    width="100%"
                                    style="max-width: 400px"
                                    :first-as-default="flows.length === 1"
                                >
                                    <template #default="{ item }">
                                        <div>
                                            {{ item.flow.label[$i18n.locale] }}
                                            <div class="create-order-form__description">
                                                {{ item.flow.description[$i18n.locale] }}
                                            </div>
                                        </div>
                                    </template>
                                </CustomSelect>
                            </FormField>
                        </Column>
                        <Column v-if="isInnerWorkspace && schema.type_productions">
                            <FormField
                                name="type_productions"
                                label="Type"
                                :required="schema.type_productions.required"
                                :error="error"
                            >
                                <CustomSelect
                                    v-model="order.type_productions"
                                    emit-prop="value"
                                    view-label="display_name"
                                    width="100%"
                                    :options="schema.type_productions.choices"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid>
                        <Column v-if="schema.creator_id" :span="6">
                            <FormField name="Creator" required :error="error" label="Creator">
                                <CustomSelect
                                    v-model="order.creator_id"
                                    :options="users"
                                    emit-prop="id"
                                    view-label="full_name"
                                    with-filter
                                    async
                                    width="100%"
                                    @filter="fetchUsers({ page: 1, search: $event })"
                                />
                            </FormField>
                        </Column>
                        <Column v-if="schema.eta" :span="2">
                            <FormField name="eta" label="Due to" :error="error">
                                <DateInput v-model="order.eta" :min-date="Date.now() - 86400000" />
                            </FormField>
                        </Column>
                        <Column :span="4">
                            <FormField name="details.isbnx" label="ISBN" :error="error">
                                <FormInput
                                    v-model="order.details.isbnx"
                                    mask="#############"
                                    type="text"
                                    placeholder="ISBN"
                                    class="form-input--compact"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid>
                        <Column :span="6">
                            <FormField
                                name="details.name"
                                label="Book Title"
                                required
                                :error="error"
                            >
                                <FormInput
                                    v-model="order.details.name"
                                    type="text"
                                    required
                                    placeholder="Book title"
                                />
                            </FormField>
                        </Column>
                        <Column :span="6">
                            <FormField
                                name="details.raw_authors"
                                label="Authors"
                                required
                                :error="error"
                            >
                                <AuthorsSelect
                                    ref="authorsRes"
                                    v-model="order.details.raw_authors"
                                    :customer="order.customer"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid>
                        <Column>
                            <FormField name="details.comment" label="Comment" :error="error">
                                <FormTextarea
                                    v-model="order.details.comment"
                                    rows="3"
                                    placeholder="Comment on the order"
                                    width="100%"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid :gap-x="40">
                        <Column :span="4">
                            <!-- @TODO parse error for nested paths -->
                            <FormField
                                name="details.book_language"
                                label="Language"
                                required
                                :error="error"
                            >
                                <LanguageSelect
                                    v-model="order.details.book_language"
                                    :languages="languages"
                                    width="100%"
                                    @select="onLanguageUpdate"
                                />
                            </FormField>
                        </Column>
                        <Column :span="4">
                            <FormField
                                name="details.speaker"
                                :error="error"
                                label="Speaker"
                                required
                            >
                                <SpeakerSelect
                                    v-model="order.details.speaker"
                                    :speakers="speakers"
                                    :show-options="
                                        !!(order.details.book_language && order.customer)
                                    "
                                    :disabled="fetchingSpeakers"
                                    :empty-text="
                                        $t(
                                            !order.customer
                                                ? 'crm.orders.selectCustomer'
                                                : !order.details.book_language
                                                  ? 'crm.orders.selectLanguage'
                                                  : 'crm.orders.noSpeakers'
                                        )
                                    "
                                    width="100%"
                                    :async="true"
                                    :can-load-more="canLoadMoreSpeakers"
                                    @filter="onSpeakerFilter"
                                    @loadmore="onSpeakerLoadMore"
                                />
                            </FormField>
                        </Column>
                        <Column :span="2">
                            <FormField name="details.volume" label="Volume" :error="error">
                                <NumberInput
                                    v-model="order.details.volume"
                                    :step="0.1"
                                    :min="0.5"
                                    :max="3"
                                />
                            </FormField>
                        </Column>
                        <Column :span="2">
                            <FormField name="details.speed" label="Speed rate" :error="error">
                                <NumberInput
                                    v-model="order.details.speed"
                                    :step="0.05"
                                    :min="0.5"
                                    :max="3"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid v-if="order.customer && commonLanguage" :gap-x="40">
                        <Column>
                            <FormField name="peronages" :error="error">
                                <template #label>
                                    <h3 v-t="'personages.manager.title'" />
                                </template>
                                <OrderPersonagesRepeater
                                    v-model="order.details.personages"
                                    :languages="languages"
                                    :limit="selectedCustomerObject.default_limit"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid>
                        <Column>
                            <FormField
                                name="include_default_speakers"
                                :required="schema.include_default_speakers.required"
                                :error="error"
                            >
                                <Checkbox
                                    v-model="order.include_default_speakers"
                                    :label="schema.include_default_speakers.label"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <Grid>
                        <Column>
                            <FormField name="document" label="File" required :error="error">
                                <FileInput
                                    ref="documentRes"
                                    style="width: 100%"
                                    accept=".docx, .epub, .fb2, .rtf, .pdf"
                                    label="Or upload the book in .docx or .epub, .fb2, .rtf"
                                    with-link
                                    @selected="setFile"
                                    @update:link="order.url = $event"
                                />
                            </FormField>
                        </Column>
                    </Grid>
                    <ErrorList :error="error" />
                    <div class="create-order__actions">
                        <Grid>
                            <Column :span="6">
                                <button
                                    :disabled="loading"
                                    type="button"
                                    class="button button--big button--fullwidth button--outlined"
                                    @click="$router.back()"
                                >
                                    Cancel Order
                                </button>
                            </Column>
                            <Column>
                                <button
                                    :disabled="loading"
                                    class="button button--big button--fullwidth"
                                >
                                    Send to approval
                                </button>
                                <button
                                    :disabled="loading"
                                    class="button button--big button--fullwidth"
                                    style="margin-top: 10px"
                                    type="button"
                                    @click.prevent="submitAndCreate"
                                >
                                    Send to approval and create another order
                                </button>
                            </Column>
                        </Grid>
                    </div>
                </form>
            </div>
        </div>
    </Layout>
</template>

<script>
import {
    FormField,
    FormInput,
    FormTextarea,
    Checkbox,
    CustomSelect,
    NumberInput,
    FileInput,
    DateInput,
    ErrorList,
} from '@speechki-frontend/shared/ui/forms';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import { mapActions, mapState, mapGetters } from 'vuex';
import AuthorsSelect from '../../components/AuthorsSelect/AuthorsSelect';
import { fetchLanguages, fetchPauseConfigs } from '@speechki-frontend/core/book/api/books';
import { fetchSpeakersPage } from '@speechki-frontend/core/editor/api/speakers';
import { createOrder, fetchCustomerFlows } from '../../api/orders';
import { fetchUsers } from '../../api/users';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import { format } from 'date-fns';
import OrderStatusBadge from '../../components/OrderStatusBadge/OrderStatusBadge';
import OrderPersonagesRepeater from '../../components/OrderPersonagesRepeater/OrderPersonagesRepeater';
import { removeEmptyFields } from '@speechki-frontend/shared/helpers/functions';
import LanguageSelect from '@speechki-frontend/shared/ui/LanguageSelect';

export default {
    name: 'OrderCreatePage',
    components: {
        Layout,
        FormInput,
        FormTextarea,
        Checkbox,
        CustomSelect,
        NumberInput,
        FileInput,
        SpeakerSelect,
        Spinner,
        DateInput,
        ErrorList,
        FormField,
        Grid,
        Column,

        AuthorsSelect,
        OrderStatusBadge,
        OrderPersonagesRepeater,
        LanguageSelect,
    },

    data() {
        return {
            order: {
                document: undefined,
                url: undefined,
                customer: undefined,
                customer_flow: undefined,
                eta: undefined,
                details: {
                    name: '',
                    comment: '',
                    isbnx: '',
                    book_language: undefined,
                    pause_config: undefined,
                    speed: 1.0,
                    volume: 1.0,
                    speaker: undefined,
                    raw_authors: [
                        {
                            first_name: '',
                            last_name: '',
                            patronymic: '',
                        },
                    ],
                    personages: [],
                },
                creator_id: undefined,
                type_productions: 'outsource',
                include_default_speakers: false,
            },
            createNew: false,

            languages: [],
            speakers: [],
            speakersInitial: [],
            speakersInitialCount: 0,
            speakersPage: 1,
            speakersTotalCount: 0,
            speakersSearch: '',
            speakersBaseParams: {},
            speakersFilterRequestId: 0,
            speakersLoadingMore: false,
            pauseConfigs: [],
            users: [],
            flows: [],

            loading: false,
            fetchingSpeakers: false,
            error: null,
            commonLanguage: undefined,
        };
    },

    computed: {
        ...mapState('order', {
            schema: (state) => state.schema.create,
            customers: (state) => state.customers,
        }),
        ...mapState('workspaces', {
            workspace: (state) => state.activeWorkspace,
        }),
        ...mapState({
            user: (state) => state.auth.user,
        }),
        ...mapGetters('workspaces', ['isInnerWorkspace', 'workspaceCustomerId']),
        arrayPauseConfigs() {
            const { locale } = this.$i18n;
            return this.pauseConfigs.map(({ id, name }) => ({ id, label: name[locale] }));
        },
        selectedCustomerObject() {
            if (!this.order.customer) return undefined;

            return this.customers.find((customer) => this.order.customer === customer.id);
        },
        canLoadMoreSpeakers() {
            return this.speakers.length < this.speakersTotalCount;
        },
    },

    watch: {
        'order.details.book_language'(value) {
            this.order.details.speaker = undefined;

            if (!value || !!this.commonLanguage) return;

            this.fetchSpeakers();
        },

        commonLanguage(value) {
            this.order.details.speaker = undefined;

            if (!value) return;

            this.fetchSpeakers({
                common_language: value,
            });
        },

        'order.customer'(customer) {
            if (!customer) return;
            this.order.customer_flow = undefined;
            if (!this.order.details.book_language) {
                this.fetchSpeakers();
            }
            this.fetchCustomerFlows();
            this.fetchLanguages({
                limit: this.selectedCustomerObject.default_limit,
            });
        },

        workspaceCustomerId: {
            immediate: true,
            async handler(customer) {
                const {
                    type_productions: { slug: typeProductions = 'outsource' },
                } = this.workspace;

                this.order.customer = customer;
                if (typeProductions) {
                    this.order.type_productions = typeProductions;
                }
                const selectedCustomer = this.customers.find((c) => c.id === customer);
                this.fetchLanguages({
                    limit: selectedCustomer.default_limit,
                });
            },
        },
    },

    async created() {
        this.loading = true;
        const promises = [this.fetchOrderSchema(), this.fetchCustomers(), this.fetchPauseConfigs()];

        if (!this.isInnerWorkspace) {
            promises.push(
                this.fetchLanguages({
                    limit: this.workspace.default_limit,
                })
            );
        }

        await Promise.all(promises);

        if (this.schema.creator_id) {
            this.order.creator_id = this.user.pk;
            this.users.push({
                id: this.user.pk,
                full_name: this.user.full_name,
            });
        }

        if (this.customers.length === 1) {
            this.order.customer = this.customers[0].id;
        }

        this.loading = false;
    },

    methods: {
        ...mapActions('order', ['fetchOrderSchema', 'fetchCustomers']),

        async fetchLanguages(params = {}) {
            this.languages = await fetchLanguages(params);
        },
        async fetchSpeakers(params = {}) {
            this.fetchingSpeakers = true;
            this.speakersPage = 1;
            this.speakersSearch = '';
            this.speakersBaseParams = {
                ...params,
                limits: this.selectedCustomerObject.default_limit,
            };
            this.order.details.speaker = undefined;
            try {
                const { results, count } = await fetchSpeakersPage({
                    page_size: 100,
                    page: 1,
                    ...this.speakersBaseParams,
                });
                this.speakers = results;
                this.speakersInitial = results;
                this.speakersInitialCount = count;
                this.speakersTotalCount = count;
            } finally {
                this.fetchingSpeakers = false;
            }
        },
        async onSpeakerFilter(search) {
            this.speakersSearch = search;
            this.speakersPage = 1;

            if (!search) {
                this.speakers = this.speakersInitial;
                this.speakersTotalCount = this.speakersInitialCount;
                return;
            }

            const requestId = ++this.speakersFilterRequestId;
            try {
                const { results, count } = await fetchSpeakersPage({
                    page_size: 100,
                    page: 1,
                    name_eng: search,
                    ...this.speakersBaseParams,
                });
                if (requestId !== this.speakersFilterRequestId) return;
                this.speakers = results;
                this.speakersTotalCount = count;
            } catch {
                // keep current speakers on error
            }
        },
        async onSpeakerLoadMore() {
            if (this.speakersLoadingMore) return;
            this.speakersLoadingMore = true;
            const nextPage = this.speakersPage + 1;
            try {
                const { results } = await fetchSpeakersPage({
                    page_size: 100,
                    page: nextPage,
                    ...(this.speakersSearch && { name_eng: this.speakersSearch }),
                    ...this.speakersBaseParams,
                });
                this.speakersPage = nextPage;
                this.speakers = [...this.speakers, ...results];
            } finally {
                this.speakersLoadingMore = false;
            }
        },
        async fetchUsers(params = { page: 1 }) {
            this.users = await fetchUsers(params);
        },
        async fetchPauseConfigs() {
            this.pauseConfigs = await fetchPauseConfigs();
        },
        async fetchCustomerFlows() {
            this.flows = await fetchCustomerFlows(this.order.customer);
        },

        setFile(file) {
            this.order.document = file;
        },

        async submitOrder() {
            this.loading = true;

            try {
                const data = new FormData();

                if (this.order.url) {
                    data.set('url', this.order.url);
                } else {
                    data.set('document', this.order.document);
                }

                const details = removeEmptyFields(this.order.details);
                details.raw_authors = details.raw_authors
                    .slice()
                    .filter((author) => {
                        if (!author.last_name && !author.first_name && !author.patronymic) {
                            return false;
                        }

                        return true;
                    })
                    .map(removeEmptyFields);

                data.set(
                    'details',
                    JSON.stringify({
                        ...details,
                    })
                );

                if (this.order.creator_id) {
                    data.set('creator_id', this.order.creator_id);
                }

                if (this.order.eta) {
                    data.set(
                        'eta',
                        format(new Date(this.order.eta), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
                    );
                }

                data.set('type_productions', this.order.type_productions);
                data.set('customer', this.order.customer);
                data.set('include_default_speakers', this.order.include_default_speakers);

                if (this.order.customer_flow) {
                    data.set('customer_flow', this.order.customer_flow);
                }

                const { id } = await createOrder(data);

                this.resetOrder();

                this.loading = false;

                if (!this.createNew) {
                    this.$router.push({
                        name: 'orders-id',
                        params: {
                            id,
                        },
                    });
                }
            } catch (e) {
                console.log(e);
                this.error = e;
                this.loading = false;
            }
        },
        async submitAndCreate() {
            this.createNew = true;
            await this.submitOrder(true);
            await this.fetchSpeakers();
            this.createNew = false;
        },
        resetOrder() {
            this.order.document = undefined;
            this.order.url = undefined;
            this.order.details.name = '';
            this.order.details.comment = '';
            this.order.details.isbnx = '';
            this.order.details.pause_config = undefined;
            this.order.details.speed = 1.0;
            this.order.details.voulme = 1.0;
            this.$refs.authorsRes.reset();
            this.$refs.documentRes.reset();
            this.speakers = [];
            if (this.order.eta) {
                this.order.eta = '';
            }
            window.scrollTo(0, 0);
        },
        onLanguageUpdate(event) {
            this.order.details.book_language = event.slug;
            this.commonLanguage = event.item.slug;
            this.order.details.speaker = null;
        },
    },
};
</script>

<style src="./OrdersCreatePage.scss" lang="scss"></style>
